import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { Store } from "../../../../api";
import { useCart } from "../../../../hooks";

import styles from "./StoreProductPage.module.scss";
import {
  buildAttributeGroups,
  buildAttributesLabel,
  findVariantBySelection,
  getCyclicImage,
  getImageIndex,
  getImageUrl,
  getProductResponse,
  getVariantAttributeMap,
  getVariantEffectivePrice,
  getVariantImages,
  groupCharacteristicsBySchema,
  isColorAttributeGroup,
} from "./StoreProductPage.form";

const SWIPE_THRESHOLD = 40;

export function StoreProductPage() {
  const { slug } = useParams();
  const location = useLocation();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [selection, setSelection] = useState({});
  const [activeImage, setActiveImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [addedMessage, setAddedMessage] = useState(false);

  const touchStartXRef = useRef(null);

  useEffect(() => {
    async function loadProduct() {
      try {
        setIsLoading(true);
        setError("");
        setAddedMessage(false);

        const storeController = new Store();
        const result = await storeController.getProduct(slug);
        const responseProduct = getProductResponse(result);

        setProduct(responseProduct);

        const firstVariant = responseProduct?.variants?.[0] || null;

        setSelection(firstVariant ? getVariantAttributeMap(firstVariant) : {});
        setQuantity(1);
      } catch (productError) {
        console.error("StoreProductPage loadProduct error:", productError);

        setProduct(null);

        setError(
          productError?.msg ||
            "No fue posible cargar este producto. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadProduct();
  }, [slug]);

  const attributeGroups = useMemo(
    () => buildAttributeGroups(product?.variants),
    [product]
  );

  const characteristicGroups = useMemo(
    () => groupCharacteristicsBySchema(product?.characteristics),
    [product]
  );

  const selectedVariant = useMemo(
    () => findVariantBySelection(product?.variants, selection),
    [product, selection]
  );

  const colorGroupName = useMemo(
    () => attributeGroups.find((group) => isColorAttributeGroup(group.name))?.name,
    [attributeGroups]
  );

  const colorMatchedVariant = useMemo(() => {
    if (!colorGroupName) {
      return null;
    }

    const selectedColor = selection[colorGroupName];

    if (!selectedColor) {
      return null;
    }

    return (
      (product?.variants || []).find(
        (variant) =>
          getVariantAttributeMap(variant)[colorGroupName] === selectedColor
      ) || null
    );
  }, [product, selection, colorGroupName]);

  const images = useMemo(
    () => getVariantImages(product, selectedVariant || colorMatchedVariant),
    [product, selectedVariant, colorMatchedVariant]
  );

  useEffect(() => {
    setActiveImage(images[0] || "");
  }, [images]);

  useEffect(() => {
    setQuantity((current) =>
      Math.min(Math.max(current, 1), Math.max(selectedVariant?.stock || 0, 1))
    );
  }, [selectedVariant]);

  function handleAttributeSelect(name, value) {
    setSelection((current) => ({ ...current, [name]: value }));
    setAddedMessage(false);
  }

  function goToImage(delta) {
    if (images.length <= 1) {
      return;
    }

    setActiveImage((current) => getCyclicImage(images, current, delta));
  }

  function handleMainImageClick() {
    goToImage(1);
  }

  function handleArrowClick(event, delta) {
    event.stopPropagation();
    goToImage(delta);
  }

  function handleTouchStart(event) {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  }

  function handleTouchEnd(event) {
    if (touchStartXRef.current === null) {
      return;
    }

    const touchEndX = event.changedTouches[0]?.clientX;
    const deltaX = touchEndX - touchStartXRef.current;

    touchStartXRef.current = null;

    if (deltaX > SWIPE_THRESHOLD) {
      goToImage(-1);
    } else if (deltaX < -SWIPE_THRESHOLD) {
      goToImage(1);
    }
  }

  function handleAddToCart() {
    if (!selectedVariant || selectedVariant.stock <= 0) {
      return;
    }

    addItem(
      {
        productId: product._id,
        variantId: selectedVariant._id,
        slug: product.slug,
        name: product.name,
        image: getImageUrl(images[0]),
        unitPrice: getVariantEffectivePrice(selectedVariant),
        stock: selectedVariant.stock,
        sku: selectedVariant.sku,
        attributesLabel: buildAttributesLabel(selection),
      },
      quantity
    );

    setAddedMessage(true);
  }

  if (isLoading) {
    return <div className={styles.StateMessage}>Cargando producto...</div>;
  }

  if (error || !product) {
    return (
      <div className={styles.StateMessage}>
        {error || "Producto no encontrado."}
      </div>
    );
  }

  const isOutOfStock = !selectedVariant || selectedVariant.stock <= 0;

  const backTo =
    location.state?.from ||
    (product.category ? `/catalogo/${product.category.slug}` : "/catalogo");

  return (
    <div className={styles.StoreProductPage}>
      <div className={styles.PageHeader}>
        <Link to={backTo} className={styles.BackLink}>
          ‹ Volver
        </Link>

        <p className={styles.Breadcrumb}>
          <Link to="/">Inicio</Link> /{" "}
          {product.category ? (
            <Link to={`/catalogo/${product.category.slug}`}>
              {product.category.name}
            </Link>
          ) : (
            <Link to="/catalogo">Catálogo</Link>
          )}{" "}
          / {product.name}
        </p>
      </div>

      <div className={styles.Layout}>
        <div className={styles.Gallery}>
          {images.length > 1 && (
            <div className={styles.Thumbnails}>
              {images.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  className={`${styles.Thumbnail} ${
                    image === activeImage ? styles.ThumbnailActive : ""
                  }`}
                  onClick={() => setActiveImage(image)}
                >
                  <img src={getImageUrl(image)} alt="" />
                </button>
              ))}
            </div>
          )}

          <div className={styles.GalleryMain}>
            <div
              className={`${styles.MainImageWrapper} ${
                images.length > 1 ? styles.MainImageWrapperClickable : ""
              }`}
              onClick={images.length > 1 ? handleMainImageClick : undefined}
              onTouchStart={images.length > 1 ? handleTouchStart : undefined}
              onTouchEnd={images.length > 1 ? handleTouchEnd : undefined}
              onKeyDown={
                images.length > 1
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleMainImageClick();
                      }
                    }
                  : undefined
              }
              role={images.length > 1 ? "button" : undefined}
              tabIndex={images.length > 1 ? 0 : undefined}
              aria-label={images.length > 1 ? "Ver siguiente foto" : undefined}
            >
              {activeImage ? (
                <>
                  <div
                    className={styles.MainImageBackdrop}
                    style={{
                      backgroundImage: `url(${getImageUrl(activeImage)})`,
                    }}
                    aria-hidden="true"
                  />

                  <img
                    src={getImageUrl(activeImage)}
                    alt={product.name}
                    className={styles.MainImage}
                    draggable={false}
                  />
                </>
              ) : (
                <span className={styles.ImagePlaceholder}>▣</span>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className={`${styles.GalleryArrow} ${styles.GalleryArrowPrev}`}
                    onClick={(event) => handleArrowClick(event, -1)}
                    aria-label="Foto anterior"
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    className={`${styles.GalleryArrow} ${styles.GalleryArrowNext}`}
                    onClick={(event) => handleArrowClick(event, 1)}
                    aria-label="Foto siguiente"
                  >
                    ›
                  </button>

                  <span className={styles.ImageCounter}>
                    {getImageIndex(images, activeImage) + 1} / {images.length}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.Info}>
          {product.brand?.name && (
            <p className={styles.Brand}>{product.brand.name}</p>
          )}

          <h1>{product.name}</h1>

          {attributeGroups.map((group) => {
            const isColor = isColorAttributeGroup(group.name);

            return (
              <div key={group.name} className={styles.AttributeGroup}>
                <span className={styles.AttributeLabel}>{group.name}</span>

                <div
                  className={
                    isColor ? styles.SwatchOptions : styles.AttributeOptions
                  }
                >
                  {group.values.map(({ label, value }) =>
                    isColor ? (
                      <button
                        key={label}
                        type="button"
                        className={`${styles.Swatch} ${
                          selection[group.name] === label
                            ? styles.SwatchActive
                            : ""
                        }`}
                        style={{ backgroundColor: value }}
                        title={label}
                        aria-label={label}
                        aria-pressed={selection[group.name] === label}
                        onClick={() => handleAttributeSelect(group.name, label)}
                      />
                    ) : (
                      <button
                        key={label}
                        type="button"
                        className={`${styles.AttributeOption} ${
                          selection[group.name] === label
                            ? styles.AttributeOptionActive
                            : ""
                        }`}
                        onClick={() => handleAttributeSelect(group.name, label)}
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}

          <div className={styles.StockRow}>
            {!selectedVariant
              ? "Selecciona las opciones para ver disponibilidad."
              : isOutOfStock
              ? "Agotado"
              : `${selectedVariant.stock} disponibles`}
          </div>

          <div className={styles.PurchaseRow}>
            <label className={styles.QuantityControl}>
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(current - 1, 1))}
                disabled={isOutOfStock}
              >
                −
              </button>

              <span>{quantity}</span>

              <button
                type="button"
                onClick={() =>
                  setQuantity((current) =>
                    Math.min(current + 1, selectedVariant?.stock || 1)
                  )
                }
                disabled={isOutOfStock}
              >
                +
              </button>
            </label>

            <button
              type="button"
              className={styles.AddToCartButton}
              onClick={handleAddToCart}
              disabled={isOutOfStock}
            >
              {isOutOfStock ? "No disponible" : "Agregar al carrito"}
            </button>
          </div>

          {addedMessage && (
            <p className={styles.AddedMessage}>
              Se agregó al carrito. <Link to="/carrito">Ver carrito →</Link>
            </p>
          )}

          {product.description && (
            <div className={styles.Description}>
              <h3>Descripción</h3>
              <p>{product.description}</p>
            </div>
          )}

          {characteristicGroups.map((group) => (
            <div key={group.schemaName} className={styles.Characteristics}>
              <h3>{group.schemaName}</h3>

              <ul>
                {group.items.map((characteristic) => (
                  <li key={characteristic._id || characteristic.name}>
                    {characteristic.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <aside className={styles.PolicyBox}>
          <div className={styles.PolicyItem}>
            <span className={styles.PolicyIcon}>
              <Icon name="truck" />
            </span>

            <div className={styles.PolicyText}>
              <p className={styles.PolicyTitle}>Envíos a toda Guatemala</p>
              <p className={styles.PolicySubtitle}>
                Capital: día después de tu compra -con mensajero
              </p>
              <p className={styles.PolicySubtitle}>
                Interior: 24 a 48hrs a través de Cargo Expreso
              </p>
            </div>
          </div>

          <div className={styles.PolicyItem}>
            <span className={styles.PolicyIcon}>
              <Icon name="lock" />
            </span>

            <div className={styles.PolicyText}>
              <p className={styles.PolicyTitle}>Formas de pago</p>
              <p className={styles.PolicySubtitle}>Depósito</p>
              <p className={styles.PolicySubtitle}>Transferencia</p>
              <p className={styles.PolicySubtitle}>Contra entrega</p>
              <p className={styles.PolicySubtitle}>Tarjeta contado</p>
              <p className={styles.PolicySubtitle}>
                Tarjeta cuotas (hasta 18 visa cuotas)
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
